// @ts-check
// Neuflora Cart Hold — theme integration entrypoint.
//
// Loaded by `cart-hold-settings.liquid` from the PDP, cart drawer, and
// main cart sections. Single file, ES module, no external deps.
//
// Responsibilities:
//
//   Day 12:
//     - Mint/read a persistent session_id in localStorage.
//     - Monkey-patch window.fetch so cart-add requests to /cart/add(.js)
//       that target a drop variant first acquire a hold from our service.
//       On 200: inject `_hold_id` + `_hold_expires_at` into the cart line
//       properties and let the cart-add proceed. On 4xx: cancel cart-add
//       and show a reason-coded toast. On service error or network failure:
//       fail open per PLAN with a non-blocking warning banner.
//
//   Day 13:
//     - Countdown UI loop: every second, scan for [data-cart-hold-line]
//       markers (emitted by cart-hold-timer.liquid), render M:SS remaining,
//       and on expiry remove the line from the Shopify cart + release the
//       hold service-side.
//     - Checkout intercept: capture click/submit on checkout buttons + cart
//       forms; fire POST /holds/:id/handoff-to-checkout for each drop hold
//       in parallel; then complete the navigation.
//     - beforeunload: fetch DELETE /holds/:id with keepalive:true for every
//       active drop hold so tab-closes free inventory promptly.
//     - Exposes window.__cartHold.api for theme code that wants the
//       primitives directly.

import {
  formatTimeRemaining,
  getSessionId,
  holdExtraProperties,
  holdFailureMessage,
  idempotencyKey,
  isCartAddUrl,
  isDropVariant,
  isHoldExpired,
  parseCartAddBody,
  parseCartHoldLines,
  reserializeCartAddBody,
} from "./cart-hold-core.js";

/**
 * @typedef {{
 *   serviceUrl: string;
 *   dropVariantIds: number[];
 *   holdDurationSec: number;
 *   debug?: boolean;
 *   messages?: Record<string, string>;
 *   _configReady?: Promise<void>;
 * }} Config
 */

/**
 * @typedef {{
 *   acquireHold(input: {
 *     variantId: number;
 *     quantity: number;
 *     unitPriceCents: number;
 *   }): Promise<
 *     | { ok: true; holdId: string; expiresAt: string; replayed?: boolean }
 *     | { ok: false; reason: string; status: number }
 *     | { ok: false; reason: "network_error" }
 *   >;
 *   handoffHold(holdId: string): Promise<{ ok: boolean }>;
 *   releaseHold(holdId: string, opts?: { keepalive?: boolean }): Promise<{ ok: boolean }>;
 *   sessionId(): string;
 * }} ApiSurface
 */

// ---------------------------------------------------------------------------
// Bootstrap moved to the bottom of this file.
//
// installCartHold calls installCountdownLoop, which references
// COUNTDOWN_TICK_MS (a `const` declared further down). `const`/`let` are
// hoisted but stay in the temporal dead zone until their declaration line
// is evaluated. Calling installCartHold here fired before line ~531 was
// reached, so installCountdownLoop crashed with
//   "Cannot access 'COUNTDOWN_TICK_MS' before initialization"
// on every page load. Running the bootstrap at the bottom of the file
// guarantees every const is live.
// ---------------------------------------------------------------------------

/**
 * @param {Window} win
 */
function installCartHold(win) {
  /** @type {{ config?: Config; api?: ApiSurface; _installed?: true; dropVariantIds?: number[] }} */
  // biome-ignore lint/suspicious/noExplicitAny: cross-realm global shape
  const cartHold = /** @type {any} */ (win).__cartHold ?? {};
  /** @type {any} */ (win).__cartHold = cartHold;

  if (cartHold._installed) return;
  cartHold._installed = true;

  // The snippet may have populated dropVariantIds as a flat array via
  // multiple .push() calls from per-product snippet emissions. Normalize.
  if (!cartHold.config) {
    cartHold.config = {
      serviceUrl: "",
      dropVariantIds: Array.isArray(cartHold.dropVariantIds) ? cartHold.dropVariantIds : [],
      holdDurationSec: 240,
    };
  } else if (Array.isArray(cartHold.dropVariantIds)) {
    cartHold.config.dropVariantIds = Array.from(
      new Set([...(cartHold.config.dropVariantIds ?? []), ...cartHold.dropVariantIds]),
    );
  }

  const config = cartHold.config;
  if (!config || !config.serviceUrl) {
    // No service URL configured → snippet wasn't included or settings
    // schema is missing the value. Don't intercept; let the theme work
    // normally.
    log(config, "init_skipped_no_service_url");
    return;
  }

  const sessionId = getSessionId(win.localStorage);
  cartHold.api = buildApi(win, config, sessionId);
  // Best-effort: load admin-configured copy overrides before anything renders
  // a message. Until it resolves, defaults apply.
  fetchConfigMessages(win, config);
  installFetchInterceptor(win, config, cartHold.api);
  installFormInterceptor(win, config, cartHold.api);
  installFormSubmitPatch(win, config, cartHold.api);
  installCountdownLoop(win, config, cartHold.api);
  installSummaryWidget(win, config);
  installStickyBar(win, config);
  installCheckoutIntercept(win, config, cartHold.api);
  installBeforeUnload(win, config, cartHold.api);

  log(config, "init_ok", {
    serviceUrl: config.serviceUrl,
    dropVariantCount: config.dropVariantIds.length,
  });
}

/**
 * Best-effort GET {serviceUrl}/config to load runtime config: admin copy
 * overrides AND the drop variant ids. Detecting drop products from `/config`
 * (rather than baking them into the page's HTML) keeps detection correct even
 * when the storefront page is served from Shopify's full-page cache or the
 * drop collection isn't published — both of which silently broke the old
 * server-rendered approach.
 *
 * Sets `config._configReady`: a promise the cart-add interceptors await so a
 * very fast add can't slip through before we know which variants are drops.
 * Failures are silent — copy falls back to defaults and dropVariantIds stays
 * whatever it was (empty → no holds, i.e. fail-open).
 * @param {Window} win
 * @param {Config} config
 */
function fetchConfigMessages(win, config) {
  config.messages = config.messages || {};
  config._configReady = (async () => {
    try {
      const r = await win.fetch(`${config.serviceUrl}/config`, {
        headers: { accept: "application/json" },
      });
      if (!r || !r.ok) return;
      const data = await r.json();
      if (data && data.messages && typeof data.messages === "object") {
        config.messages = data.messages;
      }
      if (data && Array.isArray(data.drop_variant_ids)) {
        const ids = /** @type {unknown[]} */ (data.drop_variant_ids);
        config.dropVariantIds = ids
          .map((n) => Number(n))
          .filter((n) => Number.isFinite(n) && n > 0);
        log(config, "config_drop_variants_loaded", { count: config.dropVariantIds.length });
      }
    } catch (_e) {
      /* offline / CORS / down → defaults; dropVariantIds stays as-is */
    }
  })();
}

/**
 * Await /config readiness, bounded so a hung/slow request can never block
 * add-to-cart. In practice /config resolves within ~100ms of page load, long
 * before a human can click add, so this is normally instant.
 * @param {Window} win
 * @param {Config} config
 */
async function awaitConfigReady(win, config) {
  const ready = /** @type {any} */ (config)._configReady;
  if (!ready || typeof ready.then !== "function") return;
  await Promise.race([ready, new Promise((resolve) => win.setTimeout(resolve, 1500))]);
}

// ---------------------------------------------------------------------------
// API surface
// ---------------------------------------------------------------------------

/**
 * @param {Window} win
 * @param {Config} config
 * @param {string} sessionId
 * @returns {ApiSurface}
 */
function buildApi(win, config, sessionId) {
  return {
    sessionId() {
      return sessionId;
    },
    async acquireHold(input) {
      const now = new Date();
      const body = {
        session_id: sessionId,
        variant_id: `gid://shopify/ProductVariant/${input.variantId}`,
        quantity: input.quantity,
        unit_price_cents: input.unitPriceCents,
        idempotency_key: idempotencyKey(sessionId, input.variantId, input.quantity, now),
      };
      try {
        const resp = await win.fetch(`${config.serviceUrl}/holds`, {
          method: "POST",
          headers: {
            "content-type": "application/json",
            "x-cart-hold-session": sessionId,
          },
          body: JSON.stringify(body),
        });
        const data =
          /** @type {{ ok: boolean; hold?: { hold_id: string; expires_at: string }; error?: string; replayed?: boolean }} */ (
            await resp.json().catch(() => ({ ok: false, error: "invalid_response" }))
          );
        if (resp.ok && data.ok && data.hold) {
          return {
            ok: true,
            holdId: data.hold.hold_id,
            expiresAt: data.hold.expires_at,
            replayed: Boolean(data.replayed),
          };
        }
        return { ok: false, reason: data.error ?? "service_error", status: resp.status };
      } catch (e) {
        log(config, "acquire_hold_network_error", { error: String(e) });
        return { ok: false, reason: "network_error" };
      }
    },
    async handoffHold(holdId) {
      // Extends the hold's TTL to the checkout-handoff window (15 min by
      // default service-side). Fire-and-resolve: failures don't block the
      // checkout navigation — service-side post-drop reconciliation
      // catches any drift.
      try {
        const resp = await win.fetch(
          `${config.serviceUrl}/holds/${encodeURIComponent(holdId)}/handoff-to-checkout`,
          {
            method: "POST",
            headers: {
              "content-type": "application/json",
              "x-cart-hold-session": sessionId,
            },
            body: "{}",
          },
        );
        return { ok: resp.ok };
      } catch (e) {
        log(config, "handoff_network_error", { holdId, error: String(e) });
        return { ok: false };
      }
    },
    async releaseHold(holdId, opts) {
      // Used by:
      //   - Expiry: the countdown fires this when a hold hits zero.
      //   - beforeunload: opts.keepalive=true so the browser doesn't
      //     cancel the request when the page goes away.
      try {
        const resp = await win.fetch(`${config.serviceUrl}/holds/${encodeURIComponent(holdId)}`, {
          method: "DELETE",
          headers: { "x-cart-hold-session": sessionId },
          keepalive: opts?.keepalive ?? false,
        });
        return { ok: resp.ok };
      } catch (e) {
        log(config, "release_network_error", { holdId, error: String(e) });
        return { ok: false };
      }
    },
  };
}

// ---------------------------------------------------------------------------
// Fetch interception
// ---------------------------------------------------------------------------

/**
 * @param {Window} win
 * @param {Config} config
 * @param {ApiSurface} api
 */
function installFetchInterceptor(win, config, api) {
  const origFetch = win.fetch.bind(win);

  win.fetch = async (input, init) => {
    try {
      const url = typeof input === "string" ? input : input instanceof URL ? input.href : input.url;
      if (!isCartAddUrl(url)) {
        return origFetch(input, init);
      }
      // Cart-add intercept path.
      return await handleCartAdd(win, config, api, origFetch, input, init);
    } catch (e) {
      // Defensive: any unexpected error in our wrapper falls through to
      // the original fetch (fail open).
      log(config, "interceptor_threw", { error: String(e) });
      return origFetch(input, init);
    }
  };
}

// ---------------------------------------------------------------------------
// HTML form-submit interceptor.
//
// Themes that don't use ajax cart (Prestige is the staging-deploy example
// we hit) submit /cart/add as a classic HTML form POST → 302 → /cart page.
// `window.fetch` is never called, so installFetchInterceptor above never
// fires. This handler catches those classic submits, acquires a hold the
// same way handleCartAdd does for ajax, injects the hold IDs as hidden
// inputs (`properties[_hold_id]` + `properties[_hold_expires_at]`), and
// programmatically re-submits the form so Shopify's server-side handler
// sees them.
//
// Listening at the document on the capture phase lets us run before any
// theme handler. The re-entry guard (`form._cartHoldHandled = true`)
// prevents an infinite loop when we call form.submit() ourselves.
//
// Form mode is single-variant by design (one hidden id input per form), so
// we don't need the multi-item filtering logic from handleCartAdd. Non-drop
// variants are passed through untouched.
// ---------------------------------------------------------------------------

/**
 * @param {Window} win
 * @param {Config} config
 * @param {ApiSurface} api
 */
function installFormInterceptor(win, config, api) {
  win.document.addEventListener(
    "submit",
    async (event) => {
      try {
        const form = /** @type {HTMLFormElement} */ (event.target);
        if (!(form instanceof win.HTMLFormElement)) return;

        // biome-ignore lint/suspicious/noExplicitAny: re-entry flag on element
        if (/** @type {any} */ (form)._cartHoldHandled) return;

        const action = form.getAttribute("action") || form.action || "";
        if (!isCartAddFormAction(action)) return;

        // Variant id lives in `<input name="id">` (single-variant products) OR
        // `<select name="id">` (multi-variant / sized products). Match either —
        // querying only `input[name="id"]` silently skipped every sized product.
        const idInput = /** @type {HTMLInputElement | HTMLSelectElement | null} */ (
          form.querySelector('[name="id"]')
        );
        if (!idInput) return;
        const variantId = Number.parseInt(idInput.value, 10);
        if (!Number.isFinite(variantId)) return;

        if (!isDropVariant(config, variantId)) return;

        // Drop variant → intercept.
        event.preventDefault();

        const qtyInput = /** @type {HTMLInputElement | null} */ (
          form.querySelector('input[name="quantity"]')
        );
        const quantity =
          qtyInput && Number.isFinite(Number.parseInt(qtyInput.value, 10))
            ? Number.parseInt(qtyInput.value, 10)
            : 1;

        const unitPrice = lookupVariantPriceCents(win, variantId);

        const result = await api.acquireHold({
          variantId,
          quantity,
          unitPriceCents: unitPrice,
        });

        if (!result.ok) {
          if (result.reason === "network_error") {
            showBanner(
              win,
              "Cart hold service unreachable. Add proceeding without a reservation.",
              "warning",
            );
            log(config, "form_intercept_fail_open_network");
            // biome-ignore lint/suspicious/noExplicitAny: re-entry flag on element
            /** @type {any} */ (form)._cartHoldHandled = true;
            form.submit();
            return;
          }
          // Service rejected the hold. Surface the reason; do NOT submit.
          showToast(win, holdFailureMessage(result.reason, config.messages), "error");
          log(config, "form_intercept_blocked", { reason: result.reason });
          return;
        }

        // Hold acquired. Inject the line properties as hidden inputs.
        const extras = holdExtraProperties(result.holdId, result.expiresAt);
        for (const [key, value] of Object.entries(extras)) {
          addHiddenInputToForm(form, `properties[${key}]`, value);
        }

        // biome-ignore lint/suspicious/noExplicitAny: re-entry flag on element
        /** @type {any} */ (form)._cartHoldHandled = true;
        form.submit();
      } catch (e) {
        // Defensive: any unexpected error → let the form proceed normally
        // so the cart-add isn't blocked by a bug in our interceptor.
        log(config, "form_interceptor_threw", { error: String(e) });
        // Note: we can't undo the preventDefault here. If we got past the
        // isDropVariant check before throwing, the cart-add was already
        // cancelled. Tradeoff is acceptable because every path leading to
        // a throw is also a path that wouldn't have completed cleanly.
      }
    },
    true, // capture phase — run before theme handlers
  );
}

/**
 * Patch HTMLFormElement.prototype.submit so PROGRAMMATIC form submits
 * (`form.submit()`) are intercepted too. Some themes (incl. this store's
 * multi-variant product form) add to cart by calling `form.submit()` directly,
 * which dispatches NO `submit` event — so the listener-based interceptor above
 * never sees it, and the hold's `_hold_id` never lands on the cart line
 * (confirmed live: multi-variant orders carried no `_hold_id`, single-variant
 * AJAX adds did). We defer the native submit, attach a hold, inject the line
 * properties, then submit. Non-cart-add forms pass straight through.
 *
 * @param {Window} win
 * @param {Config} config
 * @param {ApiSurface} api
 */
function installFormSubmitPatch(win, config, api) {
  const proto = win.HTMLFormElement && win.HTMLFormElement.prototype;
  // biome-ignore lint/suspicious/noExplicitAny: idempotency flag on the prototype
  if (!proto || /** @type {any} */ (proto)._cartHoldSubmitPatched) return;
  const origSubmit = proto.submit;

  proto.submit = function patchedSubmit() {
    const form = /** @type {HTMLFormElement} */ (this);
    try {
      // biome-ignore lint/suspicious/noExplicitAny: re-entry flag on element
      if (/** @type {any} */ (form)._cartHoldHandled) return origSubmit.call(form);
      const action = form.getAttribute("action") || form.action || "";
      if (!isCartAddFormAction(action)) return origSubmit.call(form);
      const idInput = /** @type {HTMLInputElement | HTMLSelectElement | null} */ (
        form.querySelector('[name="id"]')
      );
      const variantId = idInput ? Number.parseInt(idInput.value, 10) : Number.NaN;
      if (!Number.isFinite(variantId)) return origSubmit.call(form);

      // Defer the real submit: attach a hold (or block on rejection) first.
      (async () => {
        try {
          await awaitConfigReady(win, config);
          if (isDropVariant(config, variantId)) {
            const qtyInput = /** @type {HTMLInputElement | null} */ (
              form.querySelector('input[name="quantity"]')
            );
            const quantity =
              qtyInput && Number.isFinite(Number.parseInt(qtyInput.value, 10))
                ? Number.parseInt(qtyInput.value, 10)
                : 1;
            const result = await api.acquireHold({
              variantId,
              quantity,
              unitPriceCents: lookupVariantPriceCents(win, variantId),
            });
            if (result.ok) {
              const extras = holdExtraProperties(result.holdId, result.expiresAt);
              for (const [key, value] of Object.entries(extras)) {
                addHiddenInputToForm(form, `properties[${key}]`, value);
              }
            } else if (result.reason !== "network_error") {
              // Service rejected (e.g. out of stock) → block the add entirely.
              showToast(win, holdFailureMessage(result.reason, config.messages), "error");
              log(config, "form_submit_patch_blocked", { reason: result.reason });
              return; // do NOT submit
            }
            // network_error → fall through and submit without a hold (fail-open).
          }
        } catch (e) {
          log(config, "form_submit_patch_threw", { error: String(e) });
        }
        // biome-ignore lint/suspicious/noExplicitAny: re-entry flag on element
        /** @type {any} */ (form)._cartHoldHandled = true;
        origSubmit.call(form);
        /** @type {any} */ (win).__cartHold?._refreshSticky?.();
      })();
      return; // deferred — the async block performs the real submit
    } catch (_e) {
      return origSubmit.call(form);
    }
  };
  // biome-ignore lint/suspicious/noExplicitAny: idempotency flag on the prototype
  /** @type {any} */ (proto)._cartHoldSubmitPatched = true;
}

/**
 * Matches Shopify cart-add form actions. We accept the action ending in
 * `/cart/add` (HTML form). The `.js` form is the ajax variant handled by
 * the fetch interceptor.
 *
 * Action attributes can be absolute (`https://shop.com/cart/add`) or
 * path-only (`/cart/add`). URL parsing with a dummy base handles both.
 *
 * @param {string} action
 */
function isCartAddFormAction(action) {
  if (!action) return false;
  try {
    const u = new URL(action, "http://example.com");
    return /\/cart\/add\/?$/.test(u.pathname);
  } catch {
    return false;
  }
}

/**
 * Append a hidden input to a form. Used to inject
 * `properties[_hold_id]` + `properties[_hold_expires_at]` before
 * submitting.
 *
 * @param {HTMLFormElement} form
 * @param {string} name
 * @param {string} value
 */
function addHiddenInputToForm(form, name, value) {
  const input = form.ownerDocument.createElement("input");
  input.type = "hidden";
  input.name = name;
  input.value = value;
  form.appendChild(input);
}

/**
 * @param {Window} win
 * @param {Config} config
 * @param {ApiSurface} api
 * @param {typeof fetch} origFetch
 * @param {RequestInfo | URL} input
 * @param {RequestInit | undefined} init
 */
async function handleCartAdd(win, config, api, origFetch, input, init) {
  const reqInit = init ?? {};
  const contentType =
    (reqInit.headers && getHeader(reqInit.headers, "content-type")) ||
    (input instanceof Request ? input.headers.get("content-type") : null);
  const bodyForParse =
    reqInit.body ?? (input instanceof Request ? await readRequestBody(input) : null);
  const parsed = parseCartAddBody(bodyForParse, contentType);

  if (!parsed) {
    // Couldn't peek inside the body — fail open.
    log(config, "cart_add_unparseable_body");
    return origFetch(input, init);
  }

  // Drop detection comes from /config (async). Make sure it has resolved before
  // deciding whether to intercept — otherwise an add fired before /config loads
  // would miss its hold. Bounded so a hung /config can never block add-to-cart.
  await awaitConfigReady(win, config);

  // Filter to drop items only. Non-drop items get added without a hold.
  const dropItems = parsed.items.filter((item) => isDropVariant(config, item.id));
  if (dropItems.length === 0) {
    return origFetch(input, init);
  }

  // The Shopify cart-add endpoint accepts one variant per request in
  // form mode, and one or many in JSON mode. We acquire a hold per
  // drop item and collect both the holdId AND expires_at to inject as
  // cart line properties — the countdown UI reads expires_at directly
  // from cart state, no localStorage shadow ledger needed.
  /** @type {Array<Record<string, string>>} */
  const extras = new Array(parsed.items.length);
  for (let i = 0; i < parsed.items.length; i++) {
    const item = parsed.items[i];
    if (!item || !isDropVariant(config, item.id)) {
      extras[i] = {}; // non-drop item: no extras
      continue;
    }
    const unitPrice = lookupVariantPriceCents(win, item.id);
    const result = await api.acquireHold({
      variantId: item.id,
      quantity: item.quantity,
      unitPriceCents: unitPrice,
    });
    if (result.ok) {
      extras[i] = holdExtraProperties(result.holdId, result.expiresAt);
      continue;
    }
    // Failure path.
    if (result.reason === "network_error") {
      showBanner(
        win,
        "Cart hold service unreachable. Add proceeding without a reservation.",
        "warning",
      );
      log(config, "cart_add_fail_open_network");
      return origFetch(input, init);
    }
    // Service rejected. Cancel the cart-add and surface the reason.
    //
    // Mixed-cart UX (review-fix HIGH-3): if the rejected drop item is
    // part of a multi-item batch that also contains non-drop items, the
    // entire cart-add is cancelled — Shopify's /cart/add accepts an
    // all-or-nothing payload. Spell that out in the toast so the
    // customer understands why their non-drop items also didn't land.
    const base = holdFailureMessage(result.reason, config.messages);
    const isMixedBatch =
      parsed.items.length > 1 &&
      parsed.items.some((other) => other && !isDropVariant(config, other.id));
    const msg = isMixedBatch
      ? `${base} Your other items in this add weren't added either — try them again separately.`
      : base;
    showToast(win, msg, "error");
    log(config, "cart_add_blocked", { reason: result.reason, mixed: isMixedBatch });
    return new Response(
      JSON.stringify({
        ok: false,
        error: result.reason,
        message: msg,
      }),
      {
        status: 422,
        headers: { "content-type": "application/json" },
      },
    );
  }

  // All drop items got their holds. Rebuild the body with _hold_id +
  // _hold_expires_at injected on each, and re-send.
  const newBody = reserializeCartAddBody(parsed, extras);
  const newInit = { ...reqInit };
  newInit.body = newBody;
  if (parsed.shape === "form") {
    newInit.headers = setHeader(
      reqInit.headers,
      "content-type",
      "application/x-www-form-urlencoded",
    );
  } else {
    newInit.headers = setHeader(reqInit.headers, "content-type", "application/json");
  }
  // If the original was a Request object, we need to clone-with-overrides.
  const addPromise =
    input instanceof Request
      ? origFetch(new Request(input, newInit))
      : origFetch(input, newInit);
  // A hold was just created → light up the sticky bar immediately rather than
  // waiting for its next poll. Best-effort; never blocks the add response.
  addPromise.then(() => {
    const refresh = /** @type {any} */ (win).__cartHold?._refreshSticky;
    if (typeof refresh === "function") refresh();
  });
  return addPromise;
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/**
 * @param {Window} win
 * @param {number} variantId
 * @returns {number}
 */
function lookupVariantPriceCents(win, variantId) {
  // The snippet may set window.__cartHold.variantPrices = { [id]: cents }.
  /** @type {Record<number, number> | undefined} */
  const prices = /** @type {any} */ (win).__cartHold?.variantPrices;
  if (prices && Number.isFinite(prices[variantId])) {
    return /** @type {number} */ (prices[variantId]);
  }
  // Fallback: 0. Service still enforces caps but the value-cap path
  // will under-count for this hold. PLAN considers this acceptable —
  // the unit cap still applies.
  return 0;
}

/**
 * @param {HeadersInit | undefined} h
 * @param {string} name
 * @returns {string | null}
 */
function getHeader(h, name) {
  if (!h) return null;
  if (h instanceof Headers) return h.get(name);
  if (Array.isArray(h)) {
    for (const [k, v] of h) {
      if (k.toLowerCase() === name.toLowerCase()) return v;
    }
    return null;
  }
  for (const k of Object.keys(h)) {
    if (k.toLowerCase() === name.toLowerCase()) {
      return /** @type {string} */ (/** @type {Record<string, string>} */ (h)[k]);
    }
  }
  return null;
}

/**
 * @param {HeadersInit | undefined} h
 * @param {string} name
 * @param {string} value
 * @returns {Headers}
 */
function setHeader(h, name, value) {
  const out = h instanceof Headers ? new Headers(h) : new Headers(h ?? {});
  out.set(name, value);
  return out;
}

/**
 * @param {Request} req
 */
async function readRequestBody(req) {
  try {
    const clone = req.clone();
    const text = await clone.text();
    return text.length === 0 ? null : text;
  } catch {
    return null;
  }
}

// ---------------------------------------------------------------------------
// DOM helpers — minimal toast + banner. Themes can replace with their own
// by setting `window.__cartHold.toast = (msg, kind) => {...}`.
// ---------------------------------------------------------------------------

const STYLE_ID = "cart-hold-styles";

/**
 * @param {Window} win
 */
function ensureStyles(win) {
  const doc = win.document;
  if (doc.getElementById(STYLE_ID)) return;
  const style = doc.createElement("style");
  style.id = STYLE_ID;
  style.textContent = `
    .cart-hold-toast, .cart-hold-banner {
      position: fixed; right: 16px; max-width: 360px;
      padding: 12px 16px; border-radius: 6px;
      font-family: system-ui, sans-serif; font-size: 14px;
      box-shadow: 0 4px 14px rgba(0,0,0,0.15); z-index: 99999;
    }
    .cart-hold-toast { bottom: 16px; background: #fef2f2; color: #991b1b; border: 1px solid #fecaca; }
    .cart-hold-banner { top: 16px; background: #fef3c7; color: #92400e; border: 1px solid #fde68a; }
    .cart-hold-sticky {
      position: fixed; left: 0; right: 0; bottom: 0;
      display: flex; align-items: center; justify-content: space-between; gap: 12px;
      padding: 12px 16px;
      padding-right: max(16px, env(safe-area-inset-right, 0px));
      padding-bottom: calc(12px + env(safe-area-inset-bottom, 0px));
      background: #5d4e40; color: #f5efe9;
      font-family: system-ui, sans-serif; font-size: 14px; line-height: 1.3;
      box-shadow: 0 -2px 12px rgba(0,0,0,0.12); z-index: 99998;
    }
    .cart-hold-sticky__label { display: inline-flex; align-items: center; gap: 6px; min-width: 0; }
    .cart-hold-sticky__cta {
      flex: none; display: inline-flex; align-items: center; gap: 7px;
      background: #f5efe9; color: #5d4e40; text-decoration: none;
      font-weight: 600; padding: 9px 18px; border-radius: 6px; white-space: nowrap;
    }
    .cart-hold-sticky__cta svg { display: block; }
    /* Mobile-only: this is a permanent bottom bar for phones; desktop keeps the
       header cart + in-cart summary. !important beats the inline display:flex. */
    @media (min-width: 768px) { .cart-hold-sticky { display: none !important; } }
    .cart-hold-modal-overlay {
      position: fixed; inset: 0; z-index: 100000;
      background: rgba(28,27,27,0.55);
      display: flex; align-items: center; justify-content: center; padding: 20px;
      font-family: system-ui, sans-serif;
    }
    .cart-hold-modal {
      background: #f5efe9; color: #5d4e40; max-width: 380px; width: 100%;
      padding: 28px 24px; border-radius: 10px; text-align: center;
      box-shadow: 0 12px 44px rgba(0,0,0,0.32);
    }
    .cart-hold-modal__msg { font-size: 15px; line-height: 1.5; margin: 0 0 22px; }
    .cart-hold-modal__btn {
      background: #5d4e40; color: #f5efe9; border: 0; font-weight: 600; font-size: 14px;
      padding: 11px 30px; border-radius: 6px; cursor: pointer;
    }
  `;
  doc.head.appendChild(style);
}

/**
 * @param {Window} win
 * @param {string} message
 * @param {"error" | "warning"} _kind  (reserved for future styling)
 */
function showToast(win, message, _kind) {
  const userHook = /** @type {any} */ (win).__cartHold?.toast;
  if (typeof userHook === "function") return userHook(message, _kind);
  ensureStyles(win);
  const el = win.document.createElement("div");
  el.className = "cart-hold-toast";
  el.setAttribute("role", "alert");
  el.textContent = message;
  win.document.body.appendChild(el);
  win.setTimeout(() => el.remove(), 6000);
}

/**
 * @param {Window} win
 * @param {string} message
 * @param {"warning"} _kind
 */
function showBanner(win, message, _kind) {
  const userHook = /** @type {any} */ (win).__cartHold?.banner;
  if (typeof userHook === "function") return userHook(message, _kind);
  ensureStyles(win);
  const el = win.document.createElement("div");
  el.className = "cart-hold-banner";
  el.setAttribute("role", "status");
  el.textContent = message;
  win.document.body.appendChild(el);
  win.setTimeout(() => el.remove(), 8000);
}

/**
 * Centered, must-acknowledge modal shown when a hold expires — far more visible
 * than the corner banner, and it stays until the shopper dismisses it (button
 * or backdrop). Copy is admin-overridable via the `expired_modal` message.
 * @param {Window} win
 * @param {Config} config
 */
function showExpiryModal(win, config) {
  const doc = win.document;
  // Don't stack: if a modal is already open, leave it.
  if (doc.getElementById("cart-hold-expiry-modal")) return;
  // Theme may override the whole UI.
  const userHook = /** @type {any} */ (win).__cartHold?.expiryModal;
  if (typeof userHook === "function") return userHook(config);
  ensureStyles(win);
  const message =
    (config.messages && config.messages.expired_modal) ||
    "Your cart reservation expired and the item has been released. It's no longer held for you and may sell out.";
  const overlay = doc.createElement("div");
  overlay.id = "cart-hold-expiry-modal";
  overlay.className = "cart-hold-modal-overlay";
  overlay.setAttribute("role", "alertdialog");
  overlay.setAttribute("aria-modal", "true");
  overlay.innerHTML =
    '<div class="cart-hold-modal">' +
    `<p class="cart-hold-modal__msg">${escapeHtml(message)}</p>` +
    '<button type="button" class="cart-hold-modal__btn">OK</button>' +
    "</div>";
  const close = () => {
    if (overlay.parentNode) overlay.parentNode.removeChild(overlay);
  };
  const btn = /** @type {HTMLButtonElement | null} */ (
    overlay.querySelector(".cart-hold-modal__btn")
  );
  if (btn) btn.addEventListener("click", close);
  // Clicking the dark backdrop (but not the box) also dismisses.
  overlay.addEventListener("click", (e) => {
    if (e.target === overlay) close();
  });
  doc.body.appendChild(overlay);
  if (btn) btn.focus();
}

/**
 * Minimal HTML escape for merchant-configured copy that we splice into
 * innerHTML (the summary widget). Not user input, but escaping keeps a stray
 * `<` from breaking the markup.
 * @param {string} s
 * @returns {string}
 */
function escapeHtml(s) {
  return String(s)
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}

// ---------------------------------------------------------------------------
// Logging — only emits when config.debug is true.
// ---------------------------------------------------------------------------

/**
 * @param {{ debug?: boolean } | null | undefined} config
 * @param {string} event
 * @param {Record<string, unknown>} [detail]
 */
function log(config, event, detail) {
  if (!config?.debug) return;
  if (detail) console.log(`[cart-hold] ${event}`, detail);
  else console.log(`[cart-hold] ${event}`);
}

// ---------------------------------------------------------------------------
// Day 13 — Countdown loop
// ---------------------------------------------------------------------------
//
// Strategy: a single setInterval that ticks every 1s. On each tick:
//   1. Query every [data-cart-hold-line] element in the document.
//   2. For each, read the hold-id and expires-at from data-* attributes
//      (the cart-hold-timer.liquid snippet emits these directly from the
//      cart line's `properties._hold_id` + `_hold_expires_at`).
//   3. Render the remaining time as M:SS into the element's textContent.
//   4. If a hold has crossed its expiry boundary AND we haven't already
//      fired the expiry handler for this holdId in this page lifecycle,
//      fire once: remove the cart line + release the hold.
//
// Re-scanning the DOM each tick is intentional. Themes re-render the cart
// drawer via section-rendering API after every cart mutation; new markers
// appear in the document tree without us doing anything. The cost is
// negligible (cart line counts are tiny).
//
// `expiredHoldIds` is a per-page set so we don't double-fire the expiry
// handler for the same holdId if the DOM marker lingers.

const COUNTDOWN_TICK_MS = 1000;
const TIMER_SELECTOR = "[data-cart-hold-line]";

/**
 * @param {Window} win
 * @param {Config} config
 * @param {ApiSurface} api
 */
function installCountdownLoop(win, config, api) {
  /** @type {Set<string>} */
  const expiredHoldIds = new Set();

  const tick = () => {
    try {
      const now = new Date();
      const elements = win.document.querySelectorAll(TIMER_SELECTOR);
      for (const el of Array.from(elements)) {
        const holdId = el.getAttribute("data-cart-hold-id");
        const expiresAt = el.getAttribute("data-cart-hold-expires");
        const lineKey = el.getAttribute("data-cart-hold-line");
        if (!holdId || !expiresAt || !lineKey) continue;

        // Per-item timer label: admin can set timer_label with a {time}
        // placeholder; otherwise show just the M:SS reading.
        const timeStr = formatTimeRemaining(expiresAt, now);
        const tmpl = (config.messages && config.messages.timer_label) || "";
        el.textContent = tmpl.indexOf("{time}") >= 0 ? tmpl.replace("{time}", timeStr) : timeStr;

        if (isHoldExpired(expiresAt, now) && !expiredHoldIds.has(holdId)) {
          expiredHoldIds.add(holdId);
          handleHoldExpiry(win, config, api, { holdId, lineKey }).catch((e) =>
            log(config, "expiry_handler_threw", { error: String(e) }),
          );
        }
      }
    } catch (e) {
      log(config, "countdown_tick_threw", { error: String(e) });
    }
  };

  // Fire immediately so the initial render isn't a blank cell.
  tick();
  win.setInterval(tick, COUNTDOWN_TICK_MS);
}

/**
 * @param {Window} win
 * @param {Config} config
 * @param {ApiSurface} api
 * @param {{ holdId: string; lineKey: string }} ref
 */
async function handleHoldExpiry(win, config, api, ref) {
  log(config, "hold_expired", ref);
  // Remove the line from Shopify's cart by setting quantity=0 on its key.
  try {
    const resp = await win.fetch("/cart/change.js", {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({ id: ref.lineKey, quantity: 0 }),
    });
    if (!resp.ok) {
      log(config, "expiry_cart_change_failed", { status: resp.status, ...ref });
    }
  } catch (e) {
    log(config, "expiry_cart_change_threw", { error: String(e), ...ref });
  }
  // Tell the service the hold is gone (best effort).
  await api.releaseHold(ref.holdId).catch(() => undefined);
  // Surface the change to the customer with a centered, must-acknowledge modal.
  showExpiryModal(win, config);
}

// ---------------------------------------------------------------------------
// Summary widget — "Items reserved for X:XX"
// ---------------------------------------------------------------------------
//
// Aggregates the per-line timers into a single glance-able indicator at
// the top of the cart item list. Shows the EARLIEST expiry across all
// active drop holds — that's when the cart will start losing items, so
// it's the most useful number to surface.
//
// Style intent: informational, not urgency-driving. Subtle background,
// muted text, no color escalation as time runs out. The per-line timers
// already show "Reserved 0:30" per item; this widget is the "your cart"
// view of the same data.
//
// Self-injecting via JS rather than a new Liquid snippet because:
//   - Avoids requiring another Liquid edit on theme-side per-deploy.
//   - One JS file owns all cart-hold UI; cart-hold-timer.liquid stays
//     the single Liquid touchpoint for per-line state.
//   - Lets us reposition / restyle without retouching the theme.
//
// Insertion target: the parent container of the first per-line timer's
// closest cart-item wrapper. That lands the widget directly above the
// first cart line on both the cart page (sections/main-cart) and the
// drawer (snippets/cart-drawer), without theme-specific selectors.
// ---------------------------------------------------------------------------

const SUMMARY_ID = "cart-hold-summary";

/** Selectors for what looks like a "cart line item" wrapper across themes. */
const CART_LINE_WRAPPER_SELECTOR = [
  "[data-cart-line-key]",
  ".CartItem",
  ".cart-item",
  ".cart-line",
  ".CartDrawerLine",
  "li.cart-item",
].join(",");

/**
 * @param {Window} win
 * @param {Config} config
 */
function installSummaryWidget(win, config) {
  /** @type {HTMLElement | null} */
  let summaryEl = null;

  const ensureSummary = () => {
    // If we already created it AND it's still in the DOM, reuse it.
    if (summaryEl && win.document.contains(summaryEl)) return summaryEl;

    // Find a per-line timer to anchor against — also tells us this is a
    // cart-rendering page (cart, drawer, or other surface that includes
    // cart-items.liquid).
    const firstTimer = win.document.querySelector(TIMER_SELECTOR);
    if (!firstTimer) return null;

    const lineWrapper = firstTimer.closest(CART_LINE_WRAPPER_SELECTOR);
    const insertionParent = lineWrapper?.parentNode ?? null;
    if (!insertionParent) return null;
    // Anchor at the very top of the line list so the single reservation timer
    // sits above ALL items — including non-hold items in a mixed cart — rather
    // than between them. Falls back to the hold line if the list has no element
    // children for some reason.
    const insertionBefore = insertionParent.firstElementChild ?? lineWrapper;

    summaryEl = win.document.createElement("div");
    summaryEl.id = SUMMARY_ID;
    summaryEl.setAttribute("data-cart-hold-summary", "");
    summaryEl.setAttribute(
      "style",
      [
        // If the parent uses CSS grid (Prestige's `.Cart__ItemList` does),
        // span every column so the widget is a full-row banner rather than
        // a single grid cell sitting where the product image would be.
        "grid-column:1 / -1",
        // Flex-row fallback for themes that lay out items as a row of
        // inline-block columns.
        "width:100%",
        "box-sizing:border-box",
        "padding:12px 16px",
        "margin:0 0 16px 0",
        "background:rgba(28,27,27,0.04)",
        "border-radius:4px",
        "font-size:13px",
        "line-height:1.4",
        "color:#1c1b1b",
        "text-align:center",
        "letter-spacing:0.02em",
      ].join(";"),
    );
    // Admin-configurable copy: split the summary_label on {time} so the live
    // M:SS stays in its own <strong> the tick loop updates. Merchant copy is
    // HTML-escaped.
    const tmpl =
      (config.messages && config.messages.summary_label) ||
      "Items in your cart are reserved for {time}";
    const idx = tmpl.indexOf("{time}");
    const prefix = idx >= 0 ? tmpl.slice(0, idx) : `${tmpl} `;
    const suffix = idx >= 0 ? tmpl.slice(idx + "{time}".length) : "";
    summaryEl.innerHTML = `${escapeHtml(prefix)}<strong data-cart-hold-summary-time style="font-weight:600;">—</strong>${escapeHtml(suffix)}`;

    insertionParent.insertBefore(summaryEl, insertionBefore);
    log(config, "summary_widget_injected");
    return summaryEl;
  };

  const tick = () => {
    try {
      const els = win.document.querySelectorAll(TIMER_SELECTOR);
      if (els.length === 0) {
        // No active holds → hide the widget if it exists.
        if (summaryEl) summaryEl.style.display = "none";
        return;
      }

      // Find the earliest expiry across all active timers.
      let minExpiryIso = /** @type {string | null} */ (null);
      let minExpiryMs = Number.POSITIVE_INFINITY;
      for (const el of Array.from(els)) {
        const expiresAt = el.getAttribute("data-cart-hold-expires");
        if (!expiresAt) continue;
        const t = Date.parse(expiresAt);
        if (!Number.isFinite(t)) continue;
        if (t < minExpiryMs) {
          minExpiryMs = t;
          minExpiryIso = expiresAt;
        }
      }

      if (minExpiryIso === null) {
        if (summaryEl) summaryEl.style.display = "none";
        return;
      }

      const widget = ensureSummary();
      if (!widget) return;
      widget.style.display = "";

      const timeEl = widget.querySelector("[data-cart-hold-summary-time]");
      if (timeEl) {
        timeEl.textContent = formatTimeRemaining(minExpiryIso, new Date());
      }
    } catch (e) {
      log(config, "summary_tick_threw", { error: String(e) });
    }
  };

  // Fire immediately so the initial render isn't a blank cell. Then piggyback
  // on the same 1s cadence the per-line countdown uses — passing this in as
  // a separate setInterval keeps the implementations independent and reads
  // cleanly side-by-side in the bootstrap list.
  tick();
  win.setInterval(tick, COUNTDOWN_TICK_MS);
}

// ---------------------------------------------------------------------------
// Sticky bottom bar — a permanent mobile checkout bar + Checkout CTA.
//
// Shown on mobile (≤767px, enforced via media query) whenever the cart has any
// items, on every page. When a cart-hold is active the left side shows the
// live "Items reserved · M:SS" countdown; otherwise it shows a neutral cart
// label. Most shoppers are on mobile and keep browsing after adding items, so
// this keeps the cart total (and a one-tap path to checkout) — plus the hold
// urgency when relevant — in view the whole time.
//
// Unlike the summary widget — which anchors to per-line timer markers and only
// appears on cart-rendering surfaces — this derives its state from /cart.js,
// so it works on PDPs, collection pages, the home page, anywhere. The Checkout
// CTA carries `data-cart-hold-checkout`, so the existing checkout intercept
// fires the hold→checkout handoff before navigating (no separate handoff here).
// ---------------------------------------------------------------------------

const STICKY_ID = "cart-hold-sticky";

/** Re-poll /cart.js every Nth tick so adds/removes elsewhere reflect here. */
const STICKY_REFRESH_EVERY_TICKS = 3;

/**
 * @param {Window} win
 * @param {Config} config
 */
function installStickyBar(win, config) {
  /** @type {HTMLElement | null} */
  let barEl = null;
  /** Soonest hold expiry across the cart, or null when no active holds. */
  let minExpiryIso = /** @type {string | null} */ (null);
  /** Total units in the cart (all lines), shown on the Checkout CTA. */
  let itemCount = 0;

  // Left-label copy. When a hold is active we splice the live time into the
  // `sticky_label` template around {time}; otherwise we show a neutral label.
  const reservedTmpl =
    (config.messages && config.messages.sticky_label) || "Items reserved · {time}";
  const rIdx = reservedTmpl.indexOf("{time}");
  const reservedPrefix = rIdx >= 0 ? reservedTmpl.slice(0, rIdx) : `${reservedTmpl} `;
  const reservedSuffix = rIdx >= 0 ? reservedTmpl.slice(rIdx + "{time}".length) : "";
  const idleLabel =
    (config.messages && config.messages.sticky_idle_label) || "Items in your cart";

  const ensureBar = () => {
    if (barEl && win.document.contains(barEl)) return barEl;
    ensureStyles(win);
    barEl = win.document.createElement("div");
    barEl.id = STICKY_ID;
    barEl.className = "cart-hold-sticky";
    barEl.setAttribute("role", "status");
    barEl.setAttribute("aria-live", "polite");
    barEl.innerHTML =
      `<span class="cart-hold-sticky__label" data-cart-hold-sticky-label></span>` +
      `<a class="cart-hold-sticky__cta" href="/checkout" data-cart-hold-checkout aria-label="Checkout">` +
      // Exact header bag icon (theme assets/cart-icon.svg, inlined in header.liquid)
      // so the bar matches the storefront 1:1. stroke=currentColor follows the CTA colour.
      `<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true" focusable="false">` +
      `<path d="M16.0004 9V6C16.0004 3.79086 14.2095 2 12.0004 2C9.79123 2 8.00037 3.79086 8.00037 6V9M3.59237 10.352L2.99237 16.752C2.82178 18.5717 2.73648 19.4815 3.03842 20.1843C3.30367 20.8016 3.76849 21.3121 4.35839 21.6338C5.0299 22 5.94374 22 7.77142 22H16.2293C18.057 22 18.9708 22 19.6423 21.6338C20.2322 21.3121 20.6971 20.8016 20.9623 20.1843C21.2643 19.4815 21.179 18.5717 21.0084 16.752L20.4084 10.352C20.2643 8.81535 20.1923 8.04704 19.8467 7.46616C19.5424 6.95458 19.0927 6.54511 18.555 6.28984C17.9444 6 17.1727 6 15.6293 6L8.37142 6C6.82806 6 6.05638 6 5.44579 6.28984C4.90803 6.54511 4.45838 6.95458 4.15403 7.46616C3.80846 8.04704 3.73643 8.81534 3.59237 10.352Z"/>` +
      `</svg><span data-cart-hold-sticky-count></span></a>`;
    win.document.body.appendChild(barEl);
    log(config, "sticky_bar_injected");
    return barEl;
  };

  const refreshFromCart = async () => {
    try {
      const resp = await win.fetch("/cart.js", { headers: { accept: "application/json" } });
      if (!resp || !resp.ok) return;
      const cart = await resp.json();
      itemCount = typeof cart.item_count === "number" ? cart.item_count : 0;
      const holds = parseCartHoldLines(cart);
      let minMs = Number.POSITIVE_INFINITY;
      let minIso = /** @type {string | null} */ (null);
      for (const h of holds) {
        const t = Date.parse(h.expiresAtIso);
        if (Number.isFinite(t) && t < minMs) {
          minMs = t;
          minIso = h.expiresAtIso;
        }
      }
      minExpiryIso = minIso;
    } catch (e) {
      // Offline / cart fetch failed → keep the last known state.
      log(config, "sticky_refresh_threw", { error: String(e) });
    }
  };

  const tick = () => {
    // Permanent while the cart has items; only fully hidden on an empty cart.
    if (itemCount <= 0) {
      if (barEl) barEl.style.display = "none";
      return;
    }
    const now = new Date();
    const bar = ensureBar();
    bar.style.display = "flex";
    const labelEl = bar.querySelector("[data-cart-hold-sticky-label]");
    if (labelEl) {
      if (minExpiryIso && !isHoldExpired(minExpiryIso, now)) {
        labelEl.innerHTML =
          `${escapeHtml(reservedPrefix)}` +
          `<strong style="font-weight:700;">${escapeHtml(formatTimeRemaining(minExpiryIso, now))}</strong>` +
          `${escapeHtml(reservedSuffix)}`;
      } else {
        labelEl.textContent = idleLabel;
      }
    }
    const countEl = bar.querySelector("[data-cart-hold-sticky-count]");
    if (countEl) countEl.textContent = String(itemCount);
  };

  // Expose a manual refresh so other surfaces (e.g. the cart-add interceptor)
  // can light the bar up instantly after a hold is created, rather than waiting
  // for the next poll. Best-effort: callers use optional chaining.
  const ns = /** @type {any} */ (win).__cartHold || ((/** @type {any} */ (win).__cartHold = {}));
  ns._refreshSticky = () => {
    refreshFromCart().then(tick);
  };

  refreshFromCart().then(tick);
  let ticks = 0;
  win.setInterval(() => {
    ticks += 1;
    if (ticks % STICKY_REFRESH_EVERY_TICKS === 0) {
      refreshFromCart().then(tick);
    } else {
      tick();
    }
  }, COUNTDOWN_TICK_MS);
}

// ---------------------------------------------------------------------------
// Day 13 — Checkout intercept
// ---------------------------------------------------------------------------
//
// Capture clicks on checkout buttons + submits on the cart form. Before
// navigation, fire POST /holds/:id/handoff-to-checkout in parallel for
// every drop hold currently in the cart. allSettled — we don't gate
// navigation on individual handoff failures (service-side post-drop
// reconciliation catches any drift).

const CHECKOUT_BUTTON_SELECTOR = [
  "button[name=checkout]",
  "input[name=checkout]",
  "[data-cart-hold-checkout]",
  'a[href^="/checkout"]',
].join(",");

/**
 * @param {Window} win
 * @param {Config} config
 * @param {ApiSurface} api
 */
function installCheckoutIntercept(win, config, api) {
  // We attach at the document level + use capture phase so the handler
  // fires before any theme-specific click handlers can call preventDefault
  // and steal the navigation from us. We DO honor an earlier capture-phase
  // handler that called preventDefault — that's the theme telling us
  // explicitly "don't go to checkout here", which we should respect.
  win.document.addEventListener(
    "click",
    /** @type {EventListener} */
    (
      (event) => {
        // Review-fix HIGH-2: respect any capture-phase handler that
        // already cancelled this navigation.
        if (event.defaultPrevented) return;
        const target = /** @type {HTMLElement | null} */ (event.target);
        if (!target) return;
        const trigger = target.closest(CHECKOUT_BUTTON_SELECTOR);
        if (!trigger) return;
        handleCheckoutNavigation(win, config, api, event, trigger).catch((e) =>
          log(config, "checkout_intercept_threw", { error: String(e) }),
        );
      }
    ),
    true,
  );

  // Cart form submits to /checkout via a form post in many themes.
  win.document.addEventListener(
    "submit",
    /** @type {EventListener} */
    (
      (event) => {
        if (event.defaultPrevented) return;
        const target = /** @type {HTMLFormElement | null} */ (event.target);
        if (!target || target.tagName !== "FORM") return;
        const action = target.getAttribute("action") ?? "";
        if (!/\/checkout/.test(action)) return;
        handleCheckoutNavigation(win, config, api, event, target).catch((e) =>
          log(config, "checkout_submit_threw", { error: String(e) }),
        );
      }
    ),
    true,
  );
}

/**
 * @param {Window} win
 * @param {Config} config
 * @param {ApiSurface} api
 * @param {Event} event
 * @param {Element} trigger
 */
async function handleCheckoutNavigation(win, config, api, event, trigger) {
  // Snapshot cart holds BEFORE we prevent default. If there are no drop
  // holds we don't intercept at all — let the theme work normally.
  /** @type {ReturnType<typeof parseCartHoldLines>} */
  let cartHolds = [];
  try {
    const resp = await win.fetch("/cart.js", { headers: { accept: "application/json" } });
    if (resp.ok) cartHolds = parseCartHoldLines(await resp.json());
  } catch (e) {
    log(config, "checkout_intercept_cart_fetch_failed", { error: String(e) });
  }
  if (cartHolds.length === 0) return; // not a drop cart → let the click through

  event.preventDefault();
  log(config, "checkout_intercept_handoff_start", { count: cartHolds.length });

  await Promise.allSettled(cartHolds.map((h) => api.handoffHold(h.holdId)));

  log(config, "checkout_intercept_handoff_done");

  // Signal to the beforeunload handler that we're navigating to checkout;
  // it should leave the holds alone. The handoff above already extended
  // them into the "checking_out" state with the longer TTL, so a stray
  // release here would race the redirect and fail every checkout.
  // biome-ignore lint/suspicious/noExplicitAny: cross-realm global shape
  const cartHold = /** @type {any} */ (win).__cartHold;
  if (cartHold) cartHold._checkoutInFlight = true;

  // Continue the navigation the user originally intended.
  if (trigger.tagName === "FORM") {
    /** @type {HTMLFormElement} */ (trigger).submit();
    return;
  }
  if (trigger.tagName === "A") {
    const href = /** @type {HTMLAnchorElement} */ (trigger).href;
    if (href) {
      win.location.assign(href);
      return;
    }
  }
  // Buttons inside a form → re-submit the form.
  //
  // Two subtle gotchas with programmatic form submission, both observed
  // on Prestige's /cart form:
  //
  //   (1) `form.submit()` does NOT include the submitter button's name
  //       and value in the POST body. Shopify's `/cart` endpoint only
  //       redirects to /checkout when it sees `checkout=` in the POST;
  //       without that, the submit silently becomes a no-op cart update
  //       and the page stays on /cart. We inject the submitter's name+
  //       value as a hidden input before submitting.
  //
  //   (2) `form.requestSubmit(button)` is the modern alternative that
  //       includes the submitter properly, but the hidden-input approach
  //       below works in any browser and is easier to read.
  const form = trigger.closest("form");
  if (form && form.tagName === "FORM") {
    const buttonName = trigger.getAttribute("name");
    if (buttonName) {
      const buttonValue = trigger.getAttribute("value") ?? "";
      addHiddenInputToForm(
        /** @type {HTMLFormElement} */ (form),
        buttonName,
        buttonValue,
      );
    }
    /** @type {HTMLFormElement} */ (form).submit();
    return;
  }
  // Fallback: navigate to /checkout.
  win.location.assign("/checkout");
}

// ---------------------------------------------------------------------------
// Day 13 — beforeunload
// ---------------------------------------------------------------------------
//
// When the user closes the tab or navigates away, release any drop holds
// they still have. fetch with keepalive:true is the modern equivalent of
// sendBeacon and supports DELETE (sendBeacon is POST-only).

/**
 * @param {Window} win
 * @param {Config} config
 * @param {ApiSurface} api
 */
function installBeforeUnload(win, config, api) {
  /**
   * Snapshot cart holds via a SYNC XHR-free path. By unload time, /cart.js
   * is too slow — but the cart drawer renders timer elements with the
   * hold metadata in data-* attributes; we can sweep those instead.
   */
  const collect = () => {
    /** @type {Array<{ holdId: string }>} */
    const out = [];
    const seen = new Set();
    const els = win.document.querySelectorAll(TIMER_SELECTOR);
    for (const el of Array.from(els)) {
      const holdId = el.getAttribute("data-cart-hold-id");
      if (!holdId || seen.has(holdId)) continue;
      seen.add(holdId);
      out.push({ holdId });
    }
    return out;
  };

  /**
   * @param {Event} _ev
   */
  const handler = (_ev) => {
    // Skip release if we're mid-checkout. The handoff-to-checkout call has
    // already moved the hold to "checking_out" state with the extended TTL;
    // releasing here would race with checkout completion and cause every
    // checkout attempt to fail at handoff (409 hold_not_active).
    //
    // Also skip if we're navigating to a same-origin Shopify URL where the
    // hold should remain reserved (cart, checkout, products). The default
    // beforeunload-releases-everything behavior assumed pages outside the
    // shop, but real customers navigate within /cart → /checkout → /thank
    // you constantly. Server-side expiry (settings.hold_duration_seconds)
    // still catches truly abandoned holds within ~4 min.
    // biome-ignore lint/suspicious/noExplicitAny: cross-realm global shape
    const cartHold = /** @type {any} */ (win).__cartHold;
    if (cartHold?._checkoutInFlight) {
      log(config, "beforeunload_skip_checkout_in_flight");
      return;
    }
    const nav = /** @type {any} */ (win).performance?.getEntriesByType?.("navigation");
    // We can't reliably know the *next* URL from beforeunload, so the
    // best we can do is check whether we're already on a Shopify path
    // where holds shouldn't be released proactively.
    const path = win.location.pathname;
    if (
      path.startsWith("/cart") ||
      path.startsWith("/checkout") ||
      path.startsWith("/products/")
    ) {
      log(config, "beforeunload_skip_shopify_path", { path });
      return;
    }

    const holds = collect();
    if (holds.length === 0) return;
    log(config, "beforeunload_releasing", { count: holds.length });
    for (const h of holds) {
      // Fire-and-forget with keepalive:true. We deliberately don't await —
      // beforeunload is a synchronous moment from the browser's view.
      api.releaseHold(h.holdId, { keepalive: true }).catch(() => undefined);
    }
  };

  // `pagehide` is more reliable than `beforeunload` on iOS Safari.
  win.addEventListener("pagehide", handler);
  win.addEventListener("beforeunload", handler);
}

// ---------------------------------------------------------------------------
// Bootstrap (runs after every const/let in this module is initialized).
// See the comment near the top of the file for the temporal-dead-zone
// rationale that lives behind this placement.
// ---------------------------------------------------------------------------

if (typeof window !== "undefined") {
  installCartHold(window);
}
